import type * as React from 'react'
import { useCallback, useMemo } from 'react'
import { InterviewsContext } from '@/components/dashboard/providers/interviews-provider'
import {
  type ScheduledInterview,
  toClientInterviewInput,
} from '@/lib/dashboard-interviews'
import { trpc } from '@/lib/trpc'

function toError(error: unknown): Error | null {
  if (!error) return null
  if (error instanceof Error) return error
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return new Error(error.message)
  }
  return null
}

export function AdminInterviewsProvider({
  clientId,
  children,
}: {
  clientId: string
  children: React.ReactNode
}) {
  const utils = trpc.useUtils()
  const query = trpc.admin.clientInterviews.list.useQuery({ clientId })

  const {
    mutateAsync: createInterview,
    reset: resetCreateError,
    isPending: isCreating,
    error: createError,
  } = trpc.admin.clientInterviews.create.useMutation()
  const {
    mutateAsync: updateInterviewMutation,
    reset: resetUpdateError,
    isPending: isUpdating,
    error: updateError,
  } = trpc.admin.clientInterviews.update.useMutation()
  const {
    mutateAsync: removeInterviewMutation,
    reset: resetRemoveError,
    isPending: isRemoving,
    error: removeError,
  } = trpc.admin.clientInterviews.remove.useMutation()

  const invalidate = useCallback(
    () => utils.admin.clientInterviews.list.invalidate({ clientId }),
    [clientId, utils],
  )

  const resetMutationErrors = useCallback(() => {
    resetCreateError()
    resetUpdateError()
    resetRemoveError()
  }, [resetCreateError, resetRemoveError, resetUpdateError])

  const addInterview = useCallback(
    async (interview: ScheduledInterview) => {
      resetMutationErrors()
      await createInterview({
        clientId,
        interview: toClientInterviewInput(interview),
      })
      await invalidate()
    },
    [clientId, createInterview, invalidate, resetMutationErrors],
  )

  const updateInterview = useCallback(
    async (interview: ScheduledInterview) => {
      resetMutationErrors()
      await updateInterviewMutation({
        clientId,
        id: interview.id,
        interview: toClientInterviewInput(interview),
      })
      await invalidate()
    },
    [clientId, invalidate, resetMutationErrors, updateInterviewMutation],
  )

  const removeInterview = useCallback(
    async (id: string) => {
      resetMutationErrors()
      await removeInterviewMutation({ clientId, id })
      await invalidate()
    },
    [clientId, invalidate, removeInterviewMutation, resetMutationErrors],
  )

  const value = useMemo(
    () => ({
      interviews: query.data?.interviews ?? [],
      isCoachingClient: true,
      isLoading: query.isLoading,
      isSaving: isCreating || isUpdating || isRemoving,
      error: toError(query.error),
      mutationError:
        [createError, updateError, removeError]
          .map(toError)
          .find((error) => error !== null) ?? null,
      addInterview,
      updateInterview,
      removeInterview,
    }),
    [
      query.data?.interviews,
      query.isLoading,
      query.error,
      createError,
      isCreating,
      updateError,
      isUpdating,
      removeError,
      isRemoving,
      addInterview,
      updateInterview,
      removeInterview,
    ],
  )

  return (
    <InterviewsContext.Provider value={value}>
      {children}
    </InterviewsContext.Provider>
  )
}
